// eslint-disable-next-line no-restricted-imports
import { FormGroup, ControlLabel } from 'react-bootstrap';
import React, { useState, useContext, useEffect, useMemo } from 'react';
import { Media as Record, MediaType } from '~/types/api';

import QuestionTooltip from '~/App/Components/QuestionTooltip';
import { assetUrl } from '~/helpers';
import styled from '@emotion/styled';
import Switch from '~/App/Components/Switch';
import EditorContext, { EditorContextProps } from '../../Pages/Editor/Context';

import { Media } from '~/types/api';
import MediaPicker from '~/App/Modules/MediaLibrary/Components/MediaPicker';
import Modal from '~/App/Components/Modal';
import ActionButton from '../ActionButton';
import PanelSection from '../PanelSection';
import SelectedImage from '../SelectedImage';
import { useFeatures } from '~/react-hooks/use-features';
import RemoveBonjoroBadgeSwitch from '~/App/Components/RemoveBonjoroBadgeSwitch';
import { Service } from '~/types/models';

const StyledWrapper = styled.div`
  padding-top: 10px;
`;

const UploadButtonImage = styled.img`
  margin-right: 10px;
  max-width: 20px;
`;

const FlexedFormGroup = styled(FormGroup)`
  display: flex;
  justify-content: space-between;
`;

const DesignElements = () => {
  const [showBackgroundPicker, setShowBackgroundPicker] = useState(false);
  const context: EditorContextProps = useContext(EditorContext);
  const { shouldHideBonjoroBranding } = useFeatures();

  const { get, set } = context;

  const activeBackground = get([
    'landing_page',
    'background',
    'name',
  ]) as string;
  const [selectedBackground, setSelectedBackground] =
    useState(activeBackground);

  const setMediaRecordAndClose = (mediaRecord: Partial<Record>) => {
    set<Partial<Record>>('landing_page.background', mediaRecord);
    setShowBackgroundPicker(false);
  };

  const onShareableChange = (newValue: boolean) => {
    set('is_greet_shareable', newValue);
  };

  const clearSelectedBackground = () => {
    set<Record>('landing_page.background', {
      id: '',
      url: '',
      type: MediaType.Uploaded,
      name: '',
    });
  };

  const onItemDeleted = (media: Media) => {
    const currentMediaId = get(['landing_page', 'background', 'id']);

    if (currentMediaId == media.id) {
      clearSelectedBackground();
    }
  };

  const hasBonjoroBadge = useMemo(() => {
    return !shouldHideBonjoroBranding;
  }, [shouldHideBonjoroBranding]);

  useEffect(() => {
    setSelectedBackground(activeBackground);
  }, [activeBackground]);

  return (
    <StyledWrapper>
      <PanelSection>
        <ControlLabel>
          Background image &nbsp;
          <QuestionTooltip
            id={'company-logo'}
            hoverText={
              <>
                File types can be .jpg or .png.
                <br />
                Images present best at 1920px x 1200px or greater. No larger
                than 2MB in file size.
              </>
            }
          />
        </ControlLabel>

        {selectedBackground ? (
          <SelectedImage
            imageName={selectedBackground}
            onDeleteClick={clearSelectedBackground}
          />
        ) : (
          <ActionButton
            bsStyle='secondary'
            onClick={() => setShowBackgroundPicker(true)}
          >
            <UploadButtonImage
              src={assetUrl(
                'assets/images/icons/message-template/upload-turquoise.svg'
              )}
            />
            <span>Choose image</span>
          </ActionButton>
        )}
      </PanelSection>

      <FlexedFormGroup className='!mb-5'>
        <ControlLabel style={{ marginRight: 20 }}>
          Social Share &nbsp;
          <QuestionTooltip
            id={'social-share'}
            hoverText={'Hide the social sharing bar on your video landing page'}
          />
        </ControlLabel>
        <Switch
          value={get<boolean>('is_greet_shareable', true)}
          onClick={onShareableChange}
          style={{ verticalAlign: 'sub' }}
        />
      </FlexedFormGroup>

      {hasBonjoroBadge && (
        <RemoveBonjoroBadgeSwitch service={Service.VideoMessaging} />
      )}

      <Modal
        isOpen={showBackgroundPicker}
        onRequestClose={() => setShowBackgroundPicker(false)}
        className='background-media-upload'
        confirmButtonText='Done'
        cancelButtonText='Cancel'
        hideActions
      >
        <MediaPicker
          tags='background image'
          selectedId={get(['landing_page', 'background', 'id'])}
          selectedType={get(['landing_page', 'background', 'type'])}
          setMedia={setMediaRecordAndClose}
          onItemDeleted={onItemDeleted}
          onRequestClose={() => setShowBackgroundPicker(false)}
        />
      </Modal>
    </StyledWrapper>
  );
};

export default DesignElements;
